import type { RedoCartItem } from "@redotech/merchant-sdk/rpc/schema/redo-supplies/redo-cart";
import { minorUnitsToAmount } from "@redotech/money/iso4217/currencies";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoDrawer } from "@redotech/redo-web/arbiter-components/modal/redo-drawer";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as styles from "./cart-drawer.module.css";
import { useRedoSuppliesCart } from "./redo-supplies-cart-context";

function formatPrice(minorUnits: number, currency: string): string {
  const amount = minorUnitsToAmount(minorUnits, currency);
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
  }).format(amount);
}

export const CartDrawer = memo(function CartDrawer() {
  const { cart, isCartOpen, setCartOpen, refreshCart, upsertItem } =
    useRedoSuppliesCart();
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const navigate = useNavigate();
  const [cartLocked, setCartLocked] = useState(false);

  const [checkoutState, triggerCheckout] = useTriggerLoad(async () => {
    try {
      const { checkoutSessionId } = await rpcClient.createCheckoutSession({});
      setCartOpen(false);
      navigate(`shop/checkout/${checkoutSessionId}`);
    } catch {
      toast("Failed to start checkout. Please try again.", {
        variant: "error",
      });
      refreshCart();
    }
    return null;
  });

  const lockingUpsertItem: typeof upsertItem = useCallback(
    async (productId, productFamilyId, quantityDelta, productInfo) => {
      setCartLocked(true);
      try {
        await upsertItem(
          productId,
          productFamilyId,
          quantityDelta,
          productInfo,
        );
      } finally {
        setCartLocked(false);
      }
    },
    [upsertItem],
  );

  const estimatedTotal = useMemo(() => {
    if (!cart?.items.length) {
      return null;
    }
    let total = 0;
    let currency: string | null = null;
    for (const item of cart.items) {
      total += item.price * item.quantity;
      currency = item.currency;
    }
    if (currency == null) {
      return null;
    }
    return formatPrice(total, currency);
  }, [cart]);

  const handleClose = useCallback(() => setCartOpen(false), [setCartOpen]);

  const paperProps = useMemo(() => ({ className: styles.drawerPaper }), []);

  return (
    <RedoDrawer
      onModalCloseRequested={handleClose}
      open={isCartOpen}
      PaperProps={paperProps}
      showCloseButton
    >
      <Flex dir="column" gap="none" grow={1} overflow="hidden">
        <Flex
          align="center"
          borderBottomWidth="1px"
          borderColor="primary"
          borderStyle="solid"
          justify="space-between"
          p="3xl"
        >
          <Text fontSize="lg" fontWeight="semibold">
            Your cart
          </Text>
        </Flex>
        <Flex dir="column" grow={1} overflow="auto" p="xl">
          {!cart?.items.length ? (
            <Flex align="center" grow={1} justify="center">
              <Text textColor="tertiary">Your cart is empty.</Text>
            </Flex>
          ) : (
            <Flex dir="column" gap="lg">
              {cart.items.map((item) => (
                <CartItem
                  disabled={cartLocked}
                  item={item}
                  key={item.id}
                  upsertItem={lockingUpsertItem}
                />
              ))}
            </Flex>
          )}
        </Flex>
        {cart?.items.length ? (
          <Flex
            borderColor="primary"
            borderStyle="solid"
            borderTopWidth="1px"
            dir="column"
            gap="lg"
            p="3xl"
          >
            <Flex justify="space-between" w="full">
              <Text fontSize="md" fontWeight="semibold">
                Estimated total
              </Text>
              <Text fontSize="md" fontWeight="semibold">
                {estimatedTotal ?? "—"}
              </Text>
            </Flex>
            <Flex justify="flex-end">
              <RedoButton
                disabled={cartLocked}
                hierarchy="primary"
                onClick={() => triggerCheckout()}
                pending={checkoutState.pending}
                size="lg"
                text="Checkout"
              />
            </Flex>
          </Flex>
        ) : null}
      </Flex>
    </RedoDrawer>
  );
});

const CartItem = memo(function CartItem({
  disabled,
  item,
  upsertItem,
}: {
  disabled: boolean;
  item: RedoCartItem;
  upsertItem: (
    productId: string,
    productFamilyId: string,
    quantityDelta: number,
    productInfo: {
      title: string;
      price: number;
      currency: string;
      imageUrl: string | null;
      options: { name: string; value: string }[];
    },
  ) => Promise<void>;
}) {
  const productInfo = useMemo(
    () => ({
      title: item.title,
      price: item.price,
      currency: item.currency,
      imageUrl: item.imageUrl,
      options: item.options,
    }),
    [item.title, item.price, item.currency, item.imageUrl, item.options],
  );

  const [localQuantity, setLocalQuantity] = useState(item.quantity);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFlushedQuantity = useRef(item.quantity);

  // Sync local quantity when server state arrives and no debounce is pending
  useEffect(() => {
    if (!debounceTimer.current) {
      setLocalQuantity(item.quantity);
      lastFlushedQuantity.current = item.quantity;
    }
  }, [item.quantity]);

  const flushQuantityUpdate = useCallback(
    async (newQuantity: number) => {
      const delta = newQuantity - lastFlushedQuantity.current;
      lastFlushedQuantity.current = newQuantity;
      if (delta === 0) {
        return;
      }
      await upsertItem(
        item.productId,
        item.productFamilyId,
        delta,
        productInfo,
      );
    },
    [item.productId, item.productFamilyId, upsertItem, productInfo],
  );

  const handleQuantityChange = useCallback(
    (newQuantity: number) => {
      setLocalQuantity(newQuantity);
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(() => {
        debounceTimer.current = null;
        void flushQuantityUpdate(newQuantity);
      }, 300);
    },
    [flushQuantityUpdate],
  );

  const handleRemove = useCallback(async () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    await upsertItem(
      item.productId,
      item.productFamilyId,
      -lastFlushedQuantity.current,
      productInfo,
    );
  }, [item.productId, item.productFamilyId, upsertItem, productInfo]);

  const lineTotal = formatPrice(item.price * item.quantity, item.currency);

  return (
    <Flex
      align="center"
      borderBottomWidth="1px"
      borderColor="primary"
      borderStyle="solid"
      gap="lg"
      pb="lg"
    >
      {item.imageUrl && (
        <img
          alt={item.title}
          className={styles.itemImage}
          src={item.imageUrl}
        />
      )}
      <Flex dir="column" gap="xs" grow={1}>
        <Text fontSize="sm" fontWeight="semibold">
          {item.title}
        </Text>
        {item.options.length > 0 && (
          <Text fontSize="xs" textColor="tertiary">
            {item.options.map((o) => `${o.name}: ${o.value}`).join(", ")}
          </Text>
        )}
        <Text fontSize="sm" textColor="tertiary">
          {lineTotal}
        </Text>
        <Flex align="center" gap="sm">
          <RedoIncrementDecrement
            disabled={disabled}
            min={1}
            setValue={handleQuantityChange}
            size="small"
            value={localQuantity}
          />
          <RedoButton
            disabled={disabled}
            hierarchy="tertiary"
            IconLeading={TrashIcon}
            onClick={handleRemove}
            size="xs"
            theme="destructive"
          />
        </Flex>
      </Flex>
    </Flex>
  );
});
